// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@guestsupply/App/Components/Common/XngZone";
import ProductDetailsSummary from "@guestsupply/App/Components/ProductDetails/ProductDetailsSummary";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import CaretLeft from "@guestsupply/XDL/Icons/CaretLeft";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

type Props = WidgetProps & HasShellContext & ReturnType<typeof mapStateToProps> & HasProduct;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ MyOrderGuidePage?: string }>(location.search);
    const isOrderGuidePage = parsedQuery.MyOrderGuidePage;
    const wishListLinesParameter = state.pages.myListDetails.loadWishListLinesParameter;
    return {
        isOrderGuidePage,
        wishListLinesParameter,
    };
};

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    wrapper?: InjectableCss;
    backLinkWrapper?: InjectableCss;
    backLink?: LinkPresentationProps;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    bottomSection?: InjectableCss;
    separator?: InjectableCss;
    imageSection?: InjectableCss;
    productInfo?: InjectableCss;
    availabilityWrapper?: InjectableCss;
    stickyWrapper?: InjectableCss;
    badgesWrapper?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {};

const styles = viewStyles;

// XNG-Change: change structure to match design
const ProductDetailsView: React.FC<Props> = props => {
    const [stickyVisible, setStickyVisible] = React.useState(false);
    const [backUrl, setBackUrl] = React.useState("");

    const isShellEditingMood = props.shellContext?.isEditing;
    const rightSideWidget = React.useRef<HTMLDivElement>(null);

    const stickyHeader = useAppSelector(state => (state as CustomAppState).xHeader.stickyHeader || "76px");

    useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        const prevUrl = window?.localStorage?.getItem("prevUrl");
        if (prevUrl) {
            setBackUrl(
                `${prevUrl.split("&")[0]}&pdp=true&page=${props.wishListLinesParameter.page}&pageSize=${
                    props.wishListLinesParameter.pageSize && props.wishListLinesParameter.pageSize !== undefined
                        ? props.wishListLinesParameter.pageSize
                        : props.wishListLinesParameter.defaultPageSize
                }`,
            );
        }
        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    }, []);

    const handleSticky = () => {
        const isSticky = (isSticky: boolean) => {
            setStickyVisible(isSticky);
        };

        if (!rightSideWidget.current) {
            isSticky(false);
            return;
        }

        /* we need the height of the header to be excluded as we will overlap this area (add to cart button) */
        const partiallyPdpInfoHeight = rightSideWidget.current.scrollHeight - parseFloat(stickyHeader) - 30;
        if (window.scrollY >= partiallyPdpInfoHeight) {
            isSticky(true);
        } else {
            isSticky(false);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetailsView">
            {props.isOrderGuidePage && (
                <StyledWrapper {...styles.backLinkWrapper}>
                    <Link {...styles.backLink} href={backUrl} aria-label="Back">
                        {translate("Back")}
                    </Link>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.leftSection} data-test-selector="productDetails_leftColumn">
                <StyledWrapper {...styles.imageSection}>
                    <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Product Details Image" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper
                {...styles.rightSection}
                data-test-selector="productDetails_rightColumn"
                ref={rightSideWidget}
            >
                <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details Title" />
                <StyledWrapper {...styles.availabilityWrapper}>
                    <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Details Availability" />
                </StyledWrapper>
                <StyledWrapper {...styles.badgesWrapper}>
                    <XngZone
                        contentId={props.id}
                        normalWidth={true}
                        zoneName="Content3"
                        hint="Zone For Product Details Badges"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.separator}></StyledWrapper>
                <StyledWrapper>
                    <XngZone contentId={props.id} zoneName="Content4" hint="Zone For Product Details Variant Options" />
                </StyledWrapper>
                <StyledWrapper {...styles.productInfo}>
                    <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Product Details Info" />
                </StyledWrapper>
                <StyledWrapper {...styles.separator}></StyledWrapper>
                <StyledWrapper>
                    <StyledWrapper>
                        <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Product Details Actions" />
                    </StyledWrapper>
                    <StyledWrapper>
                        {createWidgetElement("ProductDetails/ProductDetailsMinQty", {
                            fields: {},
                        })}
                        <XngZone
                            contentId={props.id}
                            zoneName="Content7"
                            hint="Zone For Product Details Delivery Schedule Button"
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.separator}></StyledWrapper>

                <StyledWrapper>
                    <XngZone contentId={props.id} zoneName="Content8" hint="Zone For Product Details Description" />
                </StyledWrapper>
                {/* XNG-CHANGES GSS-830: Removing Separator when there is no Description  */}
                {!!props.product.content?.htmlContent?.length && <StyledWrapper {...styles.separator}></StyledWrapper>}
                <StyledWrapper>
                    <XngZone contentId={props.id} zoneName="Content9" hint="Zone For Product Details Share and Print" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.bottomSection}>
                <XngZone contentId={props.id} zoneName="Content10" hint="Zone For Product Specifications" />
            </StyledWrapper>
            <StyledWrapper
                className={`${stickyVisible ? "stickyVisible" : "stickyHidden"} ${
                    isShellEditingMood ? "editingMode" : ""
                }`.trim()}
                data-test-selector="productDetails-sticky-header"
                {...styles.stickyWrapper}
            >
                <Page background="common.white">
                    <ProductDetailsSummary />
                </Page>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
